/**
 * DevExtreme (esm/__internal/grids/grid_core/keyboard_navigation/m_keyboard_navigation.js)
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 * Read about DevExtreme licensing here: https://js.devexpress.com/Licensing/
 */
import _extends from "@babel/runtime/helpers/esm/extends";
import { name as clickEventName } from "../../../../common/core/events/click";
import eventsEngine from "../../../../common/core/events/core/events_engine";
import pointerEvents from "../../../../common/core/events/pointer";
import { addNamespace, createEvent, isCommandKeyPressed } from "../../../../common/core/events/utils/index";
import { noop } from "../../../../core/utils/common";
import domAdapter from "../../../../core/dom_adapter";
import { getPublicElement } from "../../../../core/element";
import $ from "../../../../core/renderer";
import browser from "../../../../core/utils/browser";
import { Deferred, when } from "../../../../core/utils/deferred";
import { getHeight, getOuterHeight, getOuterWidth, getWidth } from "../../../../core/utils/size";
import { isDeferred, isDefined, isEmptyObject } from "../../../../core/utils/type";
import * as accessibility from "../../../../ui/shared/accessibility";
import { isElementInDom } from "../../../core/utils/m_dom";
import { focused } from "../../../core/utils/m_selectors";
import { memoize } from "../../../utils/memoize";
import { EDIT_FORM_CLASS, EDIT_MODE_BATCH, EDIT_MODE_CELL, EDIT_MODE_FORM, EDIT_MODE_ROW, EDITOR_CELL_CLASS, FILTER_ROW_CLASS, FOCUSABLE_ELEMENT_SELECTOR, ROW_CLASS } from "../editing/const";
import gridCoreUtils from "../m_utils";
import { ADAPTIVE_COLUMN_NAME_CLASS, CELL_FOCUS_DISABLED_CLASS, COLUMN_HEADERS_VIEW, COMMAND_CELL_SELECTOR, COMMAND_EDIT_CLASS, COMMAND_EXPAND_CLASS, COMMAND_SELECT_CLASS, DATA_ROW_CLASS, DATEBOX_WIDGET_NAME, DRAG_COLUMN_NAME, DROPDOWN_EDITOR_OVERLAY_CLASS, EDIT_FORM_ITEM_CLASS, FAST_EDITING_DELETE_KEY, FOCUS_STATE_CLASS, FOCUS_TYPE_CELL, FOCUS_TYPE_ROW, FOCUSED_CLASS, FREESPACE_ROW_CLASS, FUNCTIONAL_KEYS, INTERACTIVE_ELEMENTS_SELECTOR, MASTER_DETAIL_CELL_CLASS, NON_FOCUSABLE_ELEMENTS_SELECTOR, REVERT_BUTTON_CLASS, ROWS_VIEW, ROWS_VIEW_CLASS, TABLE_CLASS, WIDGET_CLASS } from "./const";
import { GridCoreKeyboardNavigationDom } from "./dom";
import { KeyboardNavigationController as KeyboardNavigationControllerCore } from "./m_keyboard_navigation_core";
import { getInteractiveElement, isCellInHeaderRow, isDataRow, isDetailRow, isEditForm, isEditorCell, isEditRow, isElementDefined, isGroupFooterRow, isGroupRow, isMobile, isNotFocusedRow, shouldPreventScroll } from "./m_keyboard_navigation_utils";
import { keyboardNavigationScrollableA11yExtender } from "./scrollable_a11y";
export class KeyboardNavigationController extends KeyboardNavigationControllerCore {
  constructor() {
    super(...arguments);
    this._needNavigationToCell = false;
  }
  init() {
    this._dataController = this.getController("data");
    this._selectionController = this.getController("selection");
    this._editingController = this.getController("editing");
    this._headerPanel = this.getView("headerPanel");
    this._editorFactory = this.getController("editorFactory");
    this._focusController = this.getController("focus");
    this._adaptiveColumnsController = this.getController("adaptiveColumns");
    this._columnResizerController = this.getController("columnsResizer");
    this._rowsView = this.getView("rowsView");
    super.init();
    this._memoFireFocusedCellChanged = memoize(this._memoFireFocusedCellChanged.bind(this), {
      compareType: "value"
    });
    this._memoFireFocusedRowChanged = memoize(this._memoFireFocusedRowChanged.bind(this), {
      compareType: "value"
    });
    this.focusedHandlerWithContext = this.focusedHandlerWithContext || this.focusedHandler.bind(this);
    this.focusOutHandlerContext = this.focusOutHandlerContext ?? this.focusOutHandler.bind(this);
    this._updateFocusTimeout = null;
    this._fastEditingStarted = false;
    this._canceledCellPosition = null;
    if (this.isKeyboardEnabled()) {
      var _this$_editorFactory;
      accessibility.subscribeVisibilityChange();
      null === (_this$_editorFactory = this._editorFactory) || void 0 === _this$_editorFactory || _this$_editorFactory.focused.add(this.focusedHandlerWithContext);
    } else {
      var _this$_editorFactory2;
      accessibility.unsubscribeVisibilityChange();
      null === (_this$_editorFactory2 = this._editorFactory) || void 0 === _this$_editorFactory2 || _this$_editorFactory2.focused.remove(this.focusedHandlerWithContext);
    }
    this.initDocumentHandlers();
  }
  dispose() {
    super.dispose();
    this._resetFocusedView();
    eventsEngine.off(domAdapter.getDocument(), addNamespace(pointerEvents.down, "dxDataGridKeyboardNavigation"), this._documentClickHandler);
    clearTimeout(this._updateFocusTimeout);
    accessibility.unsubscribeVisibilityChange();
  }
  focusedHandler($element) {
    this.setupFocusedView();
    if (this._isNeedScroll) {
      if ($element.is(":visible") && this._focusedView && this._focusedView.getScrollable()) {
        this._focusedView._scrollToElement($element);
        this._isNeedScroll = false;
      }
    }
  }
  focusinHandler(event) {
    const $element = $(event.target);
    const isRelatedTargetInRowsView = $(event.relatedTarget).closest(this._rowsView.element()).length;
    const isLink = $element.is("a");
    if (event.relatedTarget && isLink && !isRelatedTargetInRowsView && this._isEventInCurrentGrid(event)) {
      let $focusedCell = this._getFocusedCell();
      $focusedCell = !isElementDefined($focusedCell) ? this._rowsView.getCellElements(0).filter("[tabindex]").eq(0) : $focusedCell;
      if (!$element.closest($focusedCell).length) {
        event.preventDefault();
        eventsEngine.trigger($focusedCell, "focus");
      }
    }
    const isCell = $element.is("td");
    const needSetFocusPosition = (this.option("focusedRowIndex") ?? -1) < 0;
    if (isCell && needSetFocusPosition) {
      this._updateFocusedCellPosition($element);
    }
  }
  focusOutHandler(e) {
    const {
      relatedTarget: relatedTarget
    } = e;
    this._toggleInertAttr(false);
    if (relatedTarget && !this.isInsideFocusedView($(relatedTarget))) {
      this._isNeedFocus = false;
      this._isHiddenFocus = false;
      this._isNeedScroll = false;
    }
  }
  subscribeToRowsViewFocusEvent() {
    var _this$_rowsView;
    const $rowsView = null === (_this$_rowsView = this._rowsView) || void 0 === _this$_rowsView ? void 0 : _this$_rowsView.element();
    eventsEngine.on($rowsView, "focusin", this.focusinHandlerContext);
    eventsEngine.on($rowsView, "focusout", this.focusOutHandlerContext);
  }
  unsubscribeFromRowsViewFocusEvent() {
    var _this$_rowsView2;
    const $rowsView = null === (_this$_rowsView2 = this._rowsView) || void 0 === _this$_rowsView2 ? void 0 : _this$_rowsView2.element();
    eventsEngine.off($rowsView, "focusin", this.focusinHandlerContext);
    eventsEngine.off($rowsView, "focusout", this.focusOutHandlerContext);
  }
  resizeCompleted() {
    var _this$_rowsView3;
    if (this.navigationToCellInProgress()) {
      this._resizeController.resetLastResizeTime();
    }
    if (!this.needToRestoreFocus) {
      return;
    }
    const scrollLeft = (null === (_this$_rowsView3 = this._rowsView) || void 0 === _this$_rowsView3 || null === (_this$_rowsView3 = _this$_rowsView3.getScrollable()) || void 0 === _this$_rowsView3 ? void 0 : _this$_rowsView3.scrollLeft()) ?? 0;
    if (!this._columnsController.isNeedToRenderVirtualColumns(scrollLeft)) {
      this.needToRestoreFocus = false;
      this.focusFirstOrLastCell();
    }
  }
  renderCompleted(e) {
    const $rowsView = this._rowsView.element();
    const isFullUpdate = !e || "refresh" === e.changeType;
    const isFocusedViewCorrect = this._focusedView && this._focusedView.name === this._rowsView.name;
    let needUpdateFocus = false;
    const isAppend = e && ("append" === e.changeType || "prepend" === e.changeType);
    const root = $(domAdapter.getRootNode($rowsView.get && $rowsView.get(0)));
    const $focusedElement = root.find(":focus");
    const isFocusedElementCorrect = this._isFocusedElementCorrect($focusedElement, $rowsView, e);
    this.unsubscribeFromRowsViewFocusEvent();
    this.subscribeToRowsViewFocusEvent();
    this.initPointerEventHandler();
    this.initKeyDownHandler();
    this._setRowsViewAttributes();
    if (isFocusedViewCorrect && isFocusedElementCorrect) {
      needUpdateFocus = this._isNeedFocus ? !isAppend : this._isHiddenFocus && isFullUpdate && !(null !== e && void 0 !== e && e.virtualColumnsScrolling);
      if (needUpdateFocus) {
        var _e$event;
        const isScrollEvent = !!(null !== e && void 0 !== e && null !== (_e$event = e.event) && void 0 !== _e$event && _e$event.type);
        const skipFocusEvent = (null === e || void 0 === e ? void 0 : e.virtualColumnsScrolling) && isScrollEvent;
        this._updateFocus(true, skipFocusEvent);
      }
    }
  }
  _isFocusedElementCorrect($focusedElement, $rowsView, e) {
    if ($focusedElement.length && !$focusedElement.closest($rowsView).length) {
      return false;
    }
    if (!$focusedElement.length && null !== e && void 0 !== e && e.virtualColumnsScrolling) {
      var _this$_focusedCellPos;
      const focusedColumnIndex = (null === (_this$_focusedCellPos = this._focusedCellPosition) || void 0 === _this$_focusedCellPos ? void 0 : _this$_focusedCellPos.columnIndex) ?? -1;
      return this._isColumnRendered(focusedColumnIndex);
    }
    return true;
  }
  initHandlers() {
    this.unsubscribeFromRowsViewFocusEvent();
    this.unsubscribeFromPointerEvent();
    super.initHandlers();
  }
  initDocumentHandlers() {
    const document = domAdapter.getDocument();
    this._documentClickHandler = this._documentClickHandler || this.createAction(e => {
      var _this$_columnResizerC;
      const $target = $(e.event.target);
      const tableSelector = `.${this.addWidgetPrefix(TABLE_CLASS)}`;
      const rowsViewSelector = `.${this.addWidgetPrefix(ROWS_VIEW_CLASS)}`;
      const editorOverlaySelector = `.${DROPDOWN_EDITOR_OVERLAY_CLASS}`;
      const needKeepFocus = !!$target.closest(tableSelector).length && !isElementInDom($target);
      if (needKeepFocus) {
        e.event.preventDefault();
        return;
      }
      const isRowsViewClick = this._isEventInCurrentGrid(e.event) && !!$target.closest(rowsViewSelector).length;
      const isEditorOverlayClick = !!$target.closest(editorOverlaySelector).length;
      const isColumnResizing = !!(null !== (_this$_columnResizerC = this._columnResizerController) && void 0 !== _this$_columnResizerC && _this$_columnResizerC.isResizing());
      if (!isRowsViewClick && !isEditorOverlayClick && !isColumnResizing) {
        const isClickOutsideFocusedView = this._focusedView ? 0 === $target.closest(this._focusedView.element()).length : true;
        if (isClickOutsideFocusedView) {
          this._resetFocusedCell(true);
        }
        this._resetFocusedView();
      }
    });
    eventsEngine.off(document, addNamespace(pointerEvents.down, "dxDataGridKeyboardNavigation"), this._documentClickHandler);
    if (this.isKeyboardEnabled()) {
      eventsEngine.on(document, addNamespace(pointerEvents.down, "dxDataGridKeyboardNavigation"), this._documentClickHandler);
    }
  }
  _setRowsViewAttributes() {
    const $rowsView = this._getRowsViewElement();
    const isGridEmpty = !this._dataController.getVisibleRows().length;
    if (isGridEmpty) {
      this._applyTabIndexToElement($rowsView);
    }
  }
  unsubscribeFromPointerEvent() {
    const pointerEventName = !isMobile() ? pointerEvents.down : clickEventName;
    const $rowsView = this._getRowsViewElement();
    this._pointerEventAction && eventsEngine.off($rowsView, addNamespace(pointerEventName, "dxDataGridKeyboardNavigation"), this._pointerEventAction);
  }
  subscribeToPointerEvent() {
    const pointerEventName = !isMobile() ? pointerEvents.down : clickEventName;
    const $rowsView = this._getRowsViewElement();
    const clickSelector = `.${ROW_CLASS} > td, .${ROW_CLASS}`;
    eventsEngine.on($rowsView, addNamespace(pointerEventName, "dxDataGridKeyboardNavigation"), clickSelector, this._pointerEventAction);
  }
  initPointerEventHandler() {
    this._pointerEventAction = this._pointerEventAction || this.createAction(this._pointerEventHandler);
    this.unsubscribeFromPointerEvent();
    this.subscribeToPointerEvent();
  }
  isRowFocusType() {
    return this.focusType === FOCUS_TYPE_ROW;
  }
  isCellFocusType() {
    return this.focusType === FOCUS_TYPE_CELL;
  }
  setRowFocusType() {
    if (this.option("focusedRowEnabled")) {
      this.focusType = FOCUS_TYPE_ROW;
    }
  }
  setCellFocusType() {
    this.focusType = FOCUS_TYPE_CELL;
  }
  keyDownHandler(e) {
    var _this$_editingControl;
    let needStopPropagation = true;
    this._isNeedFocus = true;
    this._isNeedScroll = true;
    let isHandled = this.processOnKeyDown(e);
    const isEditing = null === (_this$_editingControl = this._editingController) || void 0 === _this$_editingControl ? void 0 : _this$_editingControl.isEditing();
    const {
      originalEvent: originalEvent
    } = e;
    if (originalEvent.isDefaultPrevented()) {
      this._isNeedFocus = false;
      this._isNeedScroll = false;
      return;
    }
    !FUNCTIONAL_KEYS.includes(e.keyName) && this._updateFocusedCellPositionByTarget(originalEvent.target);
    if (!isHandled) {
      switch (e.keyName) {
        case "leftArrow":
        case "rightArrow":
          this._leftRightKeysHandler(e, isEditing);
          isHandled = true;
          break;
        case "upArrow":
        case "downArrow":
          if (e.ctrl) {
            accessibility.selectView("rowsView", this, originalEvent);
          } else {
            this._upDownKeysHandler(e, isEditing);
          }
          isHandled = true;
          break;
        case "pageUp":
        case "pageDown":
          this._pageUpDownKeyHandler(e);
          isHandled = true;
          break;
        case "space":
          isHandled = this._spaceKeyHandler(e, isEditing);
          break;
        case "A":
          if (isCommandKeyPressed(e.originalEvent)) {
            this._ctrlAKeyHandler(e, isEditing);
            isHandled = true;
          } else {
            isHandled = this._beginFastEditing(e.originalEvent);
          }
          break;
        case "tab":
          this._tabKeyHandler(e, isEditing);
          isHandled = true;
          break;
        case "enter":
          this._enterKeyHandler(e, isEditing);
          isHandled = true;
          break;
        case "escape":
          isHandled = this._escapeKeyHandler(e, isEditing);
          break;
        case "F":
          if (isCommandKeyPressed(e.originalEvent)) {
            this._ctrlFKeyHandler(e);
            isHandled = true;
          } else {
            isHandled = this._beginFastEditing(e.originalEvent);
          }
          break;
        case "F2":
          this._f2KeyHandler();
          isHandled = true;
          break;
        case "del":
        case "backspace":
          if (this._isFastEditingAllowed() && !this._isFastEditingStarted()) {
            isHandled = this._beginFastEditing(originalEvent, true);
          }
          break;
        case "home":
        case "end":
          this.homeOrEndKeyHandler(e);
      }
      if (!isHandled && !this._beginFastEditing(originalEvent)) {
        this._isNeedFocus = false;
        this._isNeedScroll = false;
        needStopPropagation = false;
      }
      if (needStopPropagation) {
        originalEvent.stopPropagation();
      }
    }
  }
  _closeEditCell() {
    const d = Deferred();
    setTimeout(() => {
      this._editingController.closeEditCell().always(d.resolve);
    });
    return d;
  }
  _leftRightKeysHandler(eventArgs, isEditing) {
    const rowIndex = this.getVisibleRowIndex();
    const $event = eventArgs.originalEvent;
    const $row = this._focusedView && this._focusedView.getRow(rowIndex);
    const directionCode = this._getDirectionCodeByKey(eventArgs.keyName);
    const isEditingNavigationMode = this._isFastEditingStarted();
    const allowNavigate = (!isEditing || isEditingNavigationMode) && isDataRow($row);
    if (allowNavigate) {
      this.setCellFocusType();
      isEditingNavigationMode && this._closeEditCell();
      if (this._isVirtualColumnRender()) {
        this._processVirtualHorizontalPosition(directionCode);
      }
      const $cell = this._getNextCell(directionCode);
      if (isElementDefined($cell)) {
        this._arrowKeysHandlerFocusCell($event, $cell, directionCode);
      }
      $event && $event.preventDefault();
    }
  }
  isInsideMasterDetail($target) {
    const $masterDetail = $target.closest(`.${MASTER_DETAIL_CELL_CLASS}`);
    return !!$masterDetail.get(0) && this.elementIsInsideGrid($masterDetail) && !$target.is($masterDetail);
  }
  _upDownKeysHandler(eventArgs, isEditing) {
    var _this$_editingControl2, _this$_editingControl3;
    const visibleRowIndex = this.getVisibleRowIndex();
    const $row = this._focusedView && this._focusedView.getRow(visibleRowIndex);
    const $event = eventArgs.originalEvent;
    const isUpArrow = "upArrow" === eventArgs.keyName;
    const dataSource = this._dataController.dataSource();
    const isRowEditingInCurrentRow = null === (_this$_editingControl2 = this._editingController) || void 0 === _this$_editingControl2 || null === (_this$_editingControl3 = _this$_editingControl2.isEditRowByIndex) || void 0 === _this$_editingControl3 ? void 0 : _this$_editingControl3.call(_this$_editingControl2, visibleRowIndex);
    const isEditingNavigationMode = this._isFastEditingStarted();
    const isInsideMasterDetail = this.isInsideMasterDetail($(null === $event || void 0 === $event ? void 0 : $event.target));
    const allowNavigate = (!isRowEditingInCurrentRow || !isEditing || isEditingNavigationMode) && $row && !isEditForm($row) && !isInsideMasterDetail;
    if (allowNavigate) {
      isEditingNavigationMode && this._closeEditCell();
      if (!this._navigateNextCell($event, eventArgs.keyName)) {
        if (this._isVirtualRowRender() && isUpArrow && dataSource && !dataSource.isLoading()) {
          const rowHeight = getOuterHeight($row);
          const rowIndex = this._focusedCellPosition.rowIndex - 1;
          this._scrollBy(0, -rowHeight, rowIndex, $event);
        }
      }
      $event && $event.preventDefault();
    }
  }
  _pageUpDownKeyHandler(eventArgs) {
    const pageIndex = this._dataController.pageIndex();
    const pageCount = this._dataController.pageCount();
    const pagingEnabled = this.option("paging.enabled");
    const isPageUp = "pageUp" === eventArgs.keyName;
    const pageStep = isPageUp ? -1 : 1;
    const scrollable = this._rowsView.getScrollable();
    if (pagingEnabled && !this._isVirtualScrolling()) {
      if ((isPageUp ? pageIndex > 0 : pageIndex < pageCount - 1) && !this._isVirtualScrolling()) {
        this._dataController.pageIndex(pageIndex + pageStep);
        eventArgs.originalEvent.preventDefault();
      }
    } else if (scrollable && getHeight(scrollable.container()) < getHeight(scrollable.$content())) {
      this._scrollBy(0, getHeight(scrollable.container()) * pageStep);
      eventArgs.originalEvent.preventDefault();
    }
  }
  _spaceKeyHandler(eventArgs, isEditing) {
    const rowIndex = this.getVisibleRowIndex();
    const $target = $(eventArgs.originalEvent && eventArgs.originalEvent.target);
    if (this.option("selection") && "none" !== this.option("selection").mode && !isEditing) {
      const isFocusedRowElement = "row" === this._getElementType($target) && this.isRowFocusType() && isDataRow($target);
      const isFocusedSelectionCell = $target.hasClass(COMMAND_SELECT_CLASS);
      if (isFocusedSelectionCell && "onClick" === this.option("selection.showCheckBoxesMode")) {
        this._selectionController.startSelectionWithCheckboxes();
      }
      if (isFocusedRowElement || $target.parent().hasClass(DATA_ROW_CLASS) || $target.hasClass(this.addWidgetPrefix(ROWS_VIEW_CLASS))) {
        this._selectionController.changeItemSelection(rowIndex, {
          shift: eventArgs.shift,
          control: eventArgs.ctrl
        });
        eventArgs.originalEvent.preventDefault();
        return true;
      }
      return false;
    }
    return this._beginFastEditing(eventArgs.originalEvent);
  }
  _ctrlAKeyHandler(eventArgs, isEditing) {
    if (!isEditing && !eventArgs.alt && "multiple" === this.option("selection.mode") && this.option("selection.allowSelectAll")) {
      this._selectionController.selectAll();
      eventArgs.originalEvent.preventDefault();
    }
  }
  _toggleInertAttr(value) {}
  _tabKeyHandler(eventArgs, isEditing) {
    const editingOptions = this.option("editing");
    const direction = eventArgs.shift ? "previous" : "next";
    const isCellPositionDefined = isDefined(this._focusedCellPosition) && !isEmptyObject(this._focusedCellPosition);
    const isFirstValidCell = eventArgs.shift && this._isFirstValidCell(this._focusedCellPosition);
    const isLastValidCell = !eventArgs.shift && this._isLastValidCell(this._focusedCellPosition);
    let isOriginalHandlerRequired = !isCellPositionDefined || isFirstValidCell || isLastValidCell;
    const eventTarget = eventArgs.originalEvent.target;
    const focusedViewElement = this._focusedView && this._focusedView.element();
    if (this._handleTabKeyOnMasterDetailCell(eventTarget, direction)) {
      return;
    }
    $(focusedViewElement).addClass(FOCUS_STATE_CLASS);
    if (editingOptions && eventTarget && !isOriginalHandlerRequired) {
      if ($(eventTarget).hasClass(this.addWidgetPrefix(ROWS_VIEW_CLASS))) {
        this._resetFocusedCell();
      }
      if (this._isVirtualColumnRender()) {
        this._processVirtualHorizontalPosition(direction);
      }
      if (isEditing) {
        if (!this._editingCellTabHandler(eventArgs, direction)) {
          return;
        }
      } else if (this._targetCellTabHandler(eventArgs, direction)) {
        isOriginalHandlerRequired = true;
      }
    }
    if (isOriginalHandlerRequired) {
      const $cell = this._getFocusedCell();
      const isCommandCell = $cell.is(COMMAND_CELL_SELECTOR);
      if (isLastValidCell && !isCommandCell) {
        this._toggleInertAttr(true);
      }
      this._editorFactory.loseFocus();
      if (this._editingController.isEditing() && !this._isRowEditMode()) {
        this._resetFocusedCell(true);
        this._resetFocusedView();
        this._closeEditCell();
      }
    } else {
      eventArgs.originalEvent.preventDefault();
    }
  }
  _getMaxVerticalOffset() {
    const scrollable = this.component.getScrollable();
    return scrollable ? scrollable.scrollHeight() - getHeight(this._rowsView.element()) : 0;
  }
  _getMaxHorizontalOffset() {
    const scrollable = this.component.getScrollable();
    return scrollable ? scrollable.scrollWidth() - getWidth(this._rowsView.element()) : 0;
  }
  _isColumnRendered(columnIndex) {
    const allVisibleColumns = this._columnsController.getVisibleColumns(null, true);
    const renderedVisibleColumns = this._columnsController.getVisibleColumns();
    const column = allVisibleColumns[columnIndex];
    let result = false;
    if (column) {
      result = renderedVisibleColumns.indexOf(column) >= 0;
    }
    return result;
  }
  _isFixedColumn(columnIndex) {
    const allVisibleColumns = this._columnsController.getVisibleColumns(null, true);
    const column = allVisibleColumns[columnIndex];
    return !!column && !!column.fixed;
  }
  _isColumnVirtual(columnIndex) {
    const localColumnIndex = columnIndex - this._columnsController.getColumnIndexOffset();
    const visibleColumns = this._columnsController.getVisibleColumns();
    const column = visibleColumns[localColumnIndex];
    return !!column && "virtual" === column.command;
  }
  _processVirtualHorizontalPosition(direction) {
    const scrollable = this.component.getScrollable();
    const columnIndex = this.getColumnIndex();
    let nextColumnIndex;
    let horizontalScrollPosition = 0;
    let needToScroll = false;
    switch (direction) {
      case "next":
      case "nextInRow":
        {
          const columnsCount = this._getVisibleColumnCount();
          nextColumnIndex = columnIndex + 1;
          horizontalScrollPosition = this.option("rtlEnabled") ? this._getMaxHorizontalOffset() : 0;
          if ("next" === direction) {
            needToScroll = columnsCount === nextColumnIndex || this._isFixedColumn(columnIndex) && !this._isColumnRendered(nextColumnIndex);
          } else {
            needToScroll = columnsCount > nextColumnIndex && this._isFixedColumn(columnIndex) && !this._isColumnRendered(nextColumnIndex);
          }
          break;
        }
      case "previous":
      case "previousInRow":
        nextColumnIndex = columnIndex - 1;
        horizontalScrollPosition = this.option("rtlEnabled") ? 0 : this._getMaxHorizontalOffset();
        if ("previous" === direction) {
          const columnIndexOffset = this._columnsController.getColumnIndexOffset();
          const leftEdgePosition = nextColumnIndex < 0 && 0 === columnIndexOffset;
          needToScroll = leftEdgePosition || this._isFixedColumn(columnIndex) && !this._isColumnRendered(nextColumnIndex);
        } else {
          needToScroll = nextColumnIndex >= 0 && this._isFixedColumn(columnIndex) && !this._isColumnRendered(nextColumnIndex);
        }
    }
    if (needToScroll) {
      scrollable.scrollTo({
        left: horizontalScrollPosition
      });
    } else if (isDefined(nextColumnIndex) && isDefined(direction) && this._isColumnVirtual(nextColumnIndex)) {
      horizontalScrollPosition = this._getHorizontalScrollPositionOffset(direction);
      0 !== horizontalScrollPosition && scrollable.scrollBy({
        left: horizontalScrollPosition,
        top: 0
      });
    }
  }
  _getHorizontalScrollPositionOffset(direction) {
    let positionOffset = 0;
    const $currentCell = this._getCell(this._focusedCellPosition);
    const currentCellWidth = $currentCell && getOuterWidth($currentCell);
    if (currentCellWidth > 0) {
      const rtlMultiplier = this.option("rtlEnabled") ? -1 : 1;
      positionOffset = "nextInRow" === direction || "next" === direction ? currentCellWidth * rtlMultiplier : currentCellWidth * rtlMultiplier * -1;
    }
    return positionOffset;
  }
  _editingCellTabHandler(eventArgs, direction) {
    const eventTarget = eventArgs.originalEvent.target;
    const $targetCell = this._getCellElementFromTarget(eventTarget);
    const isCommandCell = $targetCell.is(COMMAND_CELL_SELECTOR);
    if (this.isOriginalTabHandlerRequired($targetCell, eventArgs)) {
      return false;
    }
    if (isCommandCell) {
      return !this._targetCellTabHandler(eventArgs, direction);
    }
    this._updateFocusedCellPosition($targetCell);
    const elementType = this._getElementType(eventTarget);
    const nextCellInfo = this._getNextCellByTabKey(eventArgs.originalEvent, direction, elementType);
    const $nextCell = nextCellInfo.$cell;
    if (!$nextCell || this._handleTabKeyOnMasterDetailCell($nextCell, direction)) {
      return false;
    }
    let isEditingAllowed = false;
    const column = this._getColumnByCellElement($nextCell);
    if (null !== column && void 0 !== column && column.allowEditing) {
      const $row = $nextCell.parent();
      const rowIndex = this._getLocalRowIndex($row);
      const row = this._dataController.items()[rowIndex];
      const isDataRow = !row || "data" === row.rowType;
      isEditingAllowed = this._editingController.allowUpdating({
        row: row
      }) ? isDataRow : null === row || void 0 === row ? void 0 : row.isNewRow;
    }
    if (!isEditingAllowed) {
      this._closeEditCell();
    }
    const nextCellFocused = this._focusCell($nextCell, !nextCellInfo.isHighlighted);
    if (nextCellFocused) {
      const isRowMode = this._isRowEditMode();
      if (!isRowMode && isEditingAllowed) {
        this._editFocusedCell();
      } else {
        this._focusInteractiveElement($nextCell, eventArgs.shift);
      }
    }
    return true;
  }
  isOriginalTabHandlerRequired($cell, event) {
    const eventTarget = event.originalEvent.target;
    const elementType = this._getElementType(eventTarget);
    const $lastInteractiveElement = getInteractiveElement($cell, !event.shift);
    if ("cell" !== elementType || 0 === $lastInteractiveElement.length) {
      return false;
    }
    return eventTarget !== $lastInteractiveElement.get(0);
  }
  _targetCellTabHandler(eventArgs, direction) {
    const $event = eventArgs.originalEvent;
    let eventTarget = $event.target;
    let elementType = this._getElementType(eventTarget);
    let $cell = this._getCellElementFromTarget(eventTarget);
    if (!isEditorCell(this, $cell) && this.isOriginalTabHandlerRequired($cell, eventArgs)) {
      return true;
    }
    if (void 0 === this._focusedCellPosition.rowIndex && $(eventTarget).hasClass(ROW_CLASS)) {
      this._updateFocusedCellPosition($cell);
    }
    if (this.isRowFocusType()) {
      this.setCellFocusType();
      if ("row" === elementType && isDataRow($(eventTarget))) {
        eventTarget = this.getFirstValidCellInRow($(eventTarget));
        elementType = this._getElementType(eventTarget);
      }
    }
    const nextCellInfo = this._getNextCellByTabKey($event, direction, elementType);
    $cell = nextCellInfo.$cell;
    if (!$cell) {
      return false;
    }
    $cell = this._checkNewLineTransition($event, $cell);
    if (!$cell) {
      return false;
    }
    this._focusCell($cell, !nextCellInfo.isHighlighted);
    if (!isEditorCell(this, $cell)) {
      this._focusInteractiveElement($cell, eventArgs.shift);
    }
    return false;
  }
  _getNextCellByTabKey($event, direction, elementType) {
    let $cell = this._getNextCell(direction, elementType);
    const args = $cell && this._fireFocusedCellChanging($event, $cell, true);
    if (!args || args.cancel) {
      return {};
    }
    if (args.$newCellElement) {
      $cell = args.$newCellElement;
    }
    return {
      $cell: $cell,
      isHighlighted: args.isHighlighted
    };
  }
  _checkNewLineTransition($event, $cell) {
    const rowIndex = this.getVisibleRowIndex();
    const $row = $cell.parent();
    if (rowIndex !== this._getRowIndex($row)) {
      const cellPosition = this._getCellPosition($cell);
      const args = this._fireFocusedRowChanging($event, $row);
      if (args.cancel) {
        return;
      }
      if (args.rowIndexChanged && cellPosition) {
        this.setFocusedColumnIndex(cellPosition.columnIndex);
        $cell = this._getFocusedCell();
      }
    }
    return $cell;
  }
  _enterKeyHandler(eventArgs, isEditing) {
    var _this$_focusedView, _this$getMasterDetail;
    const rowIndex = this.getVisibleRowIndex();
    const key = this._dataController.getKeyByRowIndex(rowIndex);
    const $row = null === (_this$_focusedView = this._focusedView) || void 0 === _this$_focusedView ? void 0 : _this$_focusedView.getRow(rowIndex);
    const $cell = this._getFocusedCell();
    const needExpandGroupRow = this.option("grouping.allowCollapsing") && isGroupRow($row);
    const needExpandMasterDetailRow = this.option("masterDetail.enabled") && (null === $cell || void 0 === $cell ? void 0 : $cell.hasClass(COMMAND_EXPAND_CLASS));
    const needExpandAdaptiveRow = null === $cell || void 0 === $cell ? void 0 : $cell.hasClass(ADAPTIVE_COLUMN_NAME_CLASS);
    if (needExpandGroupRow || needExpandMasterDetailRow) {
      const item = this._dataController.items()[rowIndex];
      const isNotContinuation = (null === item || void 0 === item ? void 0 : item.data) && !item.data.isContinuation;
      if (isDefined(key) && isNotContinuation) {
        this._dataController.changeRowExpand(key);
      }
    } else if (needExpandAdaptiveRow) {
      this._adaptiveColumnsController.toggleExpandAdaptiveDetailRow(key);
      this._updateFocusedCellPosition($cell);
    } else if (null !== (_this$getMasterDetail = this.getMasterDetailCell($cell)) && void 0 !== _this$getMasterDetail && _this$getMasterDetail.is($cell)) {
      if ($cell.is(":focus")) {
        this.focusFirstInteractiveElementInside($cell);
      }
    } else if (!(null !== $cell && void 0 !== $cell && $cell.hasClass(COMMAND_EDIT_CLASS))) {
      this._processEnterKeyForDataCell(eventArgs, isEditing);
    }
  }
  focusFirstInteractiveElementInside($el) {
    $el.find(INTERACTIVE_ELEMENTS_SELECTOR).get(0).focus();
  }
  _processEnterKeyForDataCell(eventArgs, isEditing) {
    const direction = this._getEnterKeyDirection(eventArgs);
    const allowEditingOnEnterKey = this._allowEditingOnEnterKey();
    if (isEditing || !allowEditingOnEnterKey && direction) {
      this._handleEnterKeyEditingCell(eventArgs.originalEvent).done(() => {
        if ("next" === direction || "previous" === direction) {
          this._targetCellTabHandler(eventArgs, direction);
        } else if ("upArrow" === direction || "downArrow" === direction) {
          this._navigateNextCell(eventArgs.originalEvent, direction);
        }
      });
    } else if (allowEditingOnEnterKey) {
      this._startEditing(eventArgs);
    }
  }
  _getEnterKeyDirection(eventArgs) {
    const enterKeyDirection = this.option("keyboardNavigation.enterKeyDirection");
    const isShift = eventArgs.shift;
    if ("column" === enterKeyDirection) {
      return isShift ? "upArrow" : "downArrow";
    }
    if ("row" === enterKeyDirection) {
      return isShift ? "previous" : "next";
    }
    return;
  }
  _handleEnterKeyEditingCell(event) {
    const d = Deferred();
    const {
      target: target
    } = event;
    const $cell = this._getCellElementFromTarget(target);
    const isRowEditMode = this._isRowEditMode();
    this._updateFocusedCellPosition($cell);
    if (isRowEditMode) {
      this._focusEditFormCell($cell);
      setTimeout(this._editingController.saveEditData.bind(this._editingController));
      d.resolve();
    } else {
      eventsEngine.trigger($(target), "change");
      this._closeEditCell().always(d.resolve);
      event.preventDefault();
    }
    return d;
  }
  _escapeKeyHandler(eventArgs, isEditing) {
    const $cell = this._getCellElementFromTarget(eventArgs.originalEvent.target);
    if (isEditing) {
      this._updateFocusedCellPosition($cell);
      if (!this._isRowEditMode()) {
        if ("cell" === this._editingController.getEditMode()) {
          this._editingController.cancelEditData();
        } else {
          this._closeEditCell();
        }
      } else {
        this._focusEditFormCell($cell);
        this._editingController.cancelEditData();
        if (0 === this._dataController.items().length) {
          this._resetFocusedCell();
          this._editorFactory.loseFocus();
        }
      }
      eventArgs.originalEvent.preventDefault();
      return true;
    }
    const masterDetailCell = this.getMasterDetailCell($cell);
    if (masterDetailCell) {
      this._focusCell(masterDetailCell);
      return true;
    }
    return false;
  }
  _ctrlFKeyHandler(eventArgs) {
    if (this.option("searchPanel.visible")) {
      const searchTextEditor = this._headerPanel.getSearchTextEditor();
      if (searchTextEditor) {
        searchTextEditor.focus();
        eventArgs.originalEvent.preventDefault();
      }
    }
  }
  _f2KeyHandler() {
    const isEditing = this._editingController.isEditing();
    const rowIndex = this.getVisibleRowIndex();
    const $row = this._focusedView && this._focusedView.getRow(rowIndex);
    if (!isEditing && isDataRow($row)) {
      this._startEditing();
    }
  }
  _navigateNextCell($event, keyCode) {
    const $cell = this._getNextCell(keyCode);
    const directionCode = this._getDirectionCodeByKey(keyCode);
    const isCellValid = $cell && this._isCellValid($cell);
    const result = isCellValid ? this._arrowKeysHandlerFocusCell($event, $cell, directionCode) : false;
    return result;
  }
  _arrowKeysHandlerFocusCell($event, $nextCell, direction) {
    const isVerticalDirection = "prevRow" === direction || "nextRow" === direction;
    const args = this._fireFocusChangingEvents($event, $nextCell, isVerticalDirection, true);
    $nextCell = args.$newCellElement;
    if (!args.cancel && this._isCellValid($nextCell)) {
      this._focus($nextCell, !args.isHighlighted);
      return true;
    }
    return false;
  }
  _beginFastEditing(originalEvent, isDeleting) {
    if (!this._isFastEditingAllowed() || originalEvent.altKey || originalEvent.ctrlKey || this._editingController.isEditing()) {
      return false;
    }
    if (isDeleting) {
      this._startEditing(originalEvent, FAST_EDITING_DELETE_KEY);
    } else {
      const {
        key: key
      } = originalEvent;
      const keyCode = originalEvent.keyCode || originalEvent.which;
      const fastEditingKey = key || keyCode && String.fromCharCode(keyCode);
      if (fastEditingKey && (1 === fastEditingKey.length || fastEditingKey === FAST_EDITING_DELETE_KEY)) {
        this._startEditing(originalEvent, fastEditingKey);
      }
    }
    return true;
  }
  isQuickNavigationPossible() {
    var _this$_rowsView4, _this$_editingControl4, _this$_editingControl5;
    const visibleRowIndex = this.getVisibleRowIndex();
    const $row = null === (_this$_rowsView4 = this._rowsView) || void 0 === _this$_rowsView4 ? void 0 : _this$_rowsView4.getRow(visibleRowIndex);
    const dataRowTemplate = this.option("dataRowTemplate");
    const isEditRowByIndex = null === (_this$_editingControl4 = this._editingController) || void 0 === _this$_editingControl4 || null === (_this$_editingControl5 = _this$_editingControl4.isEditRowByIndex) || void 0 === _this$_editingControl5 ? void 0 : _this$_editingControl5.call(_this$_editingControl4, visibleRowIndex);
    return !isEditRowByIndex && !dataRowTemplate && isDataRow($row);
  }
  getFirstOrLastColumnIndex(needFirstColumnIndex) {
    const allVisibleColumns = this._columnsController.getVisibleColumns(null, true);
    const findColumnIndex = column => this.isFocusableColumn(column);
    return needFirstColumnIndex ? allVisibleColumns.findIndex(findColumnIndex) : allVisibleColumns.findLastIndex(findColumnIndex);
  }
  getFirstOrLastRowIndex(needFirstRow) {
    var _this$_dataController;
    const rowCount = this._isVirtualScrolling() ? this._dataController.totalItemsCount() : null === (_this$_dataController = this._dataController.items(true)) || void 0 === _this$_dataController ? void 0 : _this$_dataController.length;
    return needFirstRow ? 0 : rowCount - 1;
  }
  calculateScrollLeft(needScrollToFirstCell) {
    var _this$_columnsControl;
    const result = needScrollToFirstCell ? 0 : this._getMaxHorizontalOffset();
    const isNeedToRenderVirtualColumns = null === (_this$_columnsControl = this._columnsController) || void 0 === _this$_columnsControl ? void 0 : _this$_columnsControl.isNeedToRenderVirtualColumns(result);
    return isNeedToRenderVirtualColumns ? result : -1;
  }
  calculateScrollTop(needScrollToFirstCell) {
    const maxVerticalOffset = this._getMaxVerticalOffset();
    const hasScroll = maxVerticalOffset > 0;
    const isVirtualRowRender = this._isVirtualRowRender();
    if (isVirtualRowRender && hasScroll) {
      return needScrollToFirstCell ? 0 : maxVerticalOffset;
    }
    return -1;
  }
  scrollTo(scrollOffset) {
    var _this$_rowsView5;
    const scrollable = null === (_this$_rowsView5 = this._rowsView) || void 0 === _this$_rowsView5 ? void 0 : _this$_rowsView5.getScrollable();
    null === scrollable || void 0 === scrollable || scrollable.scrollTo(scrollOffset);
  }
  focusFirstOrLastCell(e) {
    var _this$_rowsView$getSc;
    const $cell = this._getFocusedCell();
    this._focusElement($cell, true, e);
    null === (_this$_rowsView$getSc = this._rowsView.getScrollable()) || void 0 === _this$_rowsView$getSc || _this$_rowsView$getSc.update();
  }
  navigateToFirstOrLastRow(needNavigateToFirstCell, e) {
    const scrollTop = this.calculateScrollTop(needNavigateToFirstCell);
    const firstOrLastRowIndex = this.getFirstOrLastRowIndex(needNavigateToFirstCell);
    const firstOrLastColumnIndex = this.getFirstOrLastColumnIndex(needNavigateToFirstCell);
    this.silentUpdateFocusedCellPosition({
      columnIndex: firstOrLastColumnIndex,
      rowIndex: firstOrLastRowIndex
    });
    if (scrollTop >= 0) {
      this._needNavigationToCell = true;
      this.scrollTo({
        top: scrollTop
      });
    } else {
      this.navigateToFirstOrLastCell(needNavigateToFirstCell, e);
    }
  }
  homeOrEndKeyHandler(e) {
    if (!this.isQuickNavigationPossible()) {
      return;
    }
    const needNavigateToFirstCell = "home" === e.keyName;
    const {
      originalEvent: originalEvent
    } = e;
    if (isCommandKeyPressed(originalEvent)) {
      this.navigateToFirstOrLastRow(needNavigateToFirstCell, originalEvent);
    } else {
      this.navigateToFirstOrLastCell(needNavigateToFirstCell, originalEvent);
    }
    originalEvent.preventDefault();
  }
  isFocusableColumn(column) {
    return column.type !== DRAG_COLUMN_NAME;
  }
  navigateToFirstOrLastCell(needNavigateToFirstCell, e) {
    const firstOrLastColumnIndex = this.getFirstOrLastColumnIndex(needNavigateToFirstCell);
    this._needNavigationToCell = false;
    if (firstOrLastColumnIndex < 0) {
      return;
    }
    const scrollLeft = this.calculateScrollLeft(needNavigateToFirstCell);
    this.silentUpdateFocusedCellPosition({
      columnIndex: firstOrLastColumnIndex
    });
    if (scrollLeft >= 0) {
      this.needToRestoreFocus = true;
      this.scrollTo({
        left: scrollLeft
      });
    } else {
      this.focusFirstOrLastCell(e);
    }
  }
  isQuickNavigationToFirstCell() {
    var _this$_focusedCellPos2;
    const firstColumnIndex = this.getFirstOrLastColumnIndex(true);
    return (null === (_this$_focusedCellPos2 = this._focusedCellPosition) || void 0 === _this$_focusedCellPos2 ? void 0 : _this$_focusedCellPos2.columnIndex) === firstColumnIndex;
  }
  _pointerEventHandler(e) {
    var _this$_rowsView6;
    const event = e.event || e;
    let $target = $(event.currentTarget);
    const focusedViewElement = null === (_this$_rowsView6 = this._rowsView) || void 0 === _this$_rowsView6 ? void 0 : _this$_rowsView6.element();
    const $parent = $target.parent();
    const isInteractiveElement = $(event.target).is(INTERACTIVE_ELEMENTS_SELECTOR);
    const isRevertButton = !!$(event.target).closest(`.${REVERT_BUTTON_CLASS}`).length;
    const isExpandCommandCell = $target.hasClass(COMMAND_EXPAND_CLASS);
    if (!this._isEventInCurrentGrid(event)) {
      return;
    }
    if (!isRevertButton && (this._isCellValid($target, !isInteractiveElement) || isExpandCommandCell)) {
      $target = this._isInsideEditForm($target) ? $(event.target) : $target;
      this._focusView();
      $(focusedViewElement).removeClass(FOCUS_STATE_CLASS);
      if ($parent.hasClass(FREESPACE_ROW_CLASS)) {
        this._updateFocusedCellPosition($target);
        this._applyTabIndexToElement(this._focusedView.element());
        this._focusedView.focus(true);
      } else if (!this.getMasterDetailCell($target)) {
        this._clickTargetCellHandler(event, $target);
      } else {
        this._updateFocusedCellPosition($target);
      }
    } else if ($target.is("td")) {
      this._resetFocusedCell();
    }
  }
  _clickTargetCellHandler(event, $cell) {
    const column = this._getColumnByCellElement($cell);
    const isCellEditMode = this._isCellEditMode();
    const isEditing = this._editingController.isEditing();
    this.setCellFocusType();
    const args = this._fireFocusChangingEvents(event, $cell, true);
    $cell = args.$newCellElement;
    if (!args.cancel) {
      if (args.resetFocusedRow) {
        this._focusController._resetFocusedRow();
        return;
      }
      if (args.rowIndexChanged) {
        $cell = this._getFocusedCell();
      }
      if (!args.isHighlighted && !isCellEditMode) {
        this.setRowFocusType();
      }
      this._updateFocusedCellPosition($cell);
      if (this._allowRowUpdating() && isCellEditMode && column && column.allowEditing) {
        this._isNeedFocus = false;
        this._isHiddenFocus = false;
      } else {
        $cell = this._getFocusedCell();
        const $target = event && $(event.target).closest(`${NON_FOCUSABLE_ELEMENTS_SELECTOR}, td`);
        const skipFocusEvent = $target && $target.not($cell).is(NON_FOCUSABLE_ELEMENTS_SELECTOR);
        const isEditCell = !(null !== column && void 0 !== column && column.command) && isEditing && $cell.hasClass(EDITOR_CELL_CLASS);
        const isDisabled = !isEditCell && (!args.isHighlighted || skipFocusEvent);
        this._focus($cell, isDisabled, skipFocusEvent);
      }
    } else {
      this.setRowFocusType();
      this.setFocusedRowIndex(args.prevRowIndex);
      if (isEditing && isCellEditMode) {
        this._closeEditCell();
      }
    }
  }
  _allowRowUpdating() {
    const rowIndex = this.getVisibleRowIndex();
    const row = this._dataController.items()[rowIndex];
    return this._editingController.allowUpdating({
      row: row
    }, "click");
  }
  focus(element) {
    let activeElementSelector;
    const focusedRowEnabled = this.option("focusedRowEnabled");
    const isHighlighted = this._isCellElement($(element));
    if (!element) {
      activeElementSelector = `.${this.addWidgetPrefix(ROWS_VIEW_CLASS)} .dx-row[tabindex]`;
      if (!focusedRowEnabled) {
        activeElementSelector += `, .${this.addWidgetPrefix(ROWS_VIEW_CLASS)} .dx-row > td[tabindex]`;
      }
      element = this.component.$element().find(activeElementSelector).first();
    }
    element && this._focusElement($(element), isHighlighted);
  }
  getFocusedView() {
    return this.getView("rowsView");
  }
  setupFocusedView() {
    if (this.isKeyboardEnabled() && !isDefined(this._focusedView)) {
      this._focusView();
    }
  }
  _focusElement($element, isHighlighted, event) {
    const rowsViewElement = $(this._getRowsViewElement());
    const $focusedView = $element.closest(rowsViewElement);
    const isRowFocusType = this.isRowFocusType();
    let args = {};
    if (!$focusedView.length || this._isCellElement($element) && !this._isCellValid($element)) {
      return;
    }
    this._focusView();
    this._isNeedFocus = true;
    this._isNeedScroll = true;
    if (this._isCellElement($element) || isGroupRow($element)) {
      this.setCellFocusType();
      args = this._fireFocusChangingEvents(event, $element, true, isHighlighted);
      $element = args.$newCellElement;
      if (isRowFocusType && !args.isHighlighted) {
        this.setRowFocusType();
      }
    }
    if (!args.cancel) {
      this._focus($element, !args.isHighlighted);
      if ("row" !== this._getElementType($element) || isEditRow($element)) {
        this._focusInteractiveElement($element);
      }
    }
  }
  isInsideFocusedView($element) {
    var _this$_focusedView2;
    return 0 !== $element.closest(null === (_this$_focusedView2 = this._focusedView) || void 0 === _this$_focusedView2 ? void 0 : _this$_focusedView2.element()).length;
  }
  _focusView() {
    this._focusedView = this._rowsView;
  }
  _resetFocusedView() {
    this.setRowFocusType();
    this._focusedView = null;
  }
  _focusInteractiveElement($cell, isLast) {
    if (!$cell) {
      return;
    }
    const $focusedElement = getInteractiveElement($cell, isLast);
    gridCoreUtils.focusAndSelectElement(this, $focusedElement);
  }
  _focus($cell, disableFocus, skipFocusEvent) {
    const $row = $cell && !$cell.hasClass(ROW_CLASS) ? $cell.closest(`.${ROW_CLASS}`) : $cell;
    if ($row && isNotFocusedRow($row)) {
      return;
    }
    const focusedView = this._focusedView;
    const $focusViewElement = focusedView && focusedView.element();
    let $focusElement;
    this._isHiddenFocus = disableFocus;
    const isRowFocus = isGroupRow($row) || isGroupFooterRow($row) || this.isRowFocusType();
    if (isRowFocus) {
      $focusElement = $row;
      if (focusedView) {
        this.setFocusedRowIndex(this._getRowIndex($row));
      }
    } else if (this._isCellElement($cell)) {
      $focusElement = $cell;
      this._updateFocusedCellPosition($cell);
    }
    if ($focusElement) {
      if ($focusViewElement) {
        $focusViewElement.find(".dx-row[tabindex], .dx-row > td[tabindex]").filter((i, node) => gridCoreUtils.isElementInCurrentGrid(this, $(node))).not($focusElement).removeClass(CELL_FOCUS_DISABLED_CLASS).removeClass(FOCUSED_CLASS).removeAttr("tabindex");
      }
      eventsEngine.one($focusElement, "blur", e => {
        if (e.relatedTarget) {
          $focusElement.removeClass(CELL_FOCUS_DISABLED_CLASS).removeClass(FOCUSED_CLASS);
        }
      });
      if (!skipFocusEvent) {
        this._applyTabIndexToElement($focusElement);
        eventsEngine.trigger($focusElement, "focus");
      }
      if (disableFocus) {
        $focusElement.addClass(CELL_FOCUS_DISABLED_CLASS);
        if (isRowFocus) {
          $cell.addClass(CELL_FOCUS_DISABLED_CLASS);
        }
      } else {
        this._editorFactory.focus($focusElement);
      }
    }
  }
  _updateFocus(isRenderView) {
    let skipFocusEvent = arguments.length > 1 && void 0 !== arguments[1] ? arguments[1] : false;
    this._updateFocusTimeout = setTimeout(() => {
      if (this._needFocusEditingCell()) {
        this._editingController._focusEditingCell();
        return;
      }
      let $cell = this._getFocusedCell();
      const isEditing = this._editingController.isEditing();
      if (!this.getMasterDetailCell($cell) || this._isRowEditMode()) {
        if (this._hasSkipRow($cell.parent())) {
          const direction = this._focusedCellPosition && this._focusedCellPosition.rowIndex > 0 ? "upArrow" : "downArrow";
          $cell = this._getNextCell(direction);
        }
        if (isElementDefined($cell)) {
          if ($cell.is("td") || $cell.hasClass(this.addWidgetPrefix(EDIT_FORM_ITEM_CLASS))) {
            const isCommandCell = $cell.is(COMMAND_CELL_SELECTOR);
            const $focusedElementInsideCell = $cell.find(":focus");
            const isFocusedElementDefined = isElementDefined($focusedElementInsideCell);
            const column = this._getColumnByCellElement($cell);
            if ((isRenderView || !isCommandCell) && this._editorFactory.focus()) {
              if (isCommandCell && isFocusedElementDefined) {
                gridCoreUtils.focusAndSelectElement(this, $focusedElementInsideCell);
                return;
              }
              !isFocusedElementDefined && this._focus($cell, false, skipFocusEvent);
            } else if (!isFocusedElementDefined && (this._isNeedFocus || this._isHiddenFocus)) {
              this._focus($cell, this._isHiddenFocus, skipFocusEvent);
            }
            if (isEditing && !(null !== column && void 0 !== column && column.showEditorAlways)) {
              this._focusInteractiveElement.bind(this)($cell);
            }
          } else {
            eventsEngine.trigger($cell, "focus");
          }
        }
      }
    });
  }
  _needFocusEditingCell() {
    const isCellEditMode = this._editingController.getEditMode() === EDIT_MODE_CELL;
    const isBatchEditMode = this._editingController.getEditMode() === EDIT_MODE_BATCH;
    const cellEditModeHasChanges = isCellEditMode && this._editingController.hasChanges();
    const isNewRowBatchEditMode = isBatchEditMode && this._editingController.isNewRowInEditMode();
    const $cell = this._getFocusedCell();
    return (0 === $cell.children().length || $cell.find(FOCUSABLE_ELEMENT_SELECTOR).length > 0) && (cellEditModeHasChanges || isNewRowBatchEditMode);
  }
  _updateFocusedCellPositionByTarget(target) {
    var _this$_focusedCellPos3;
    const elementType = this._getElementType(target);
    if ("row" === elementType && isDefined(null === (_this$_focusedCellPos3 = this._focusedCellPosition) || void 0 === _this$_focusedCellPos3 ? void 0 : _this$_focusedCellPos3.columnIndex)) {
      const $row = $(target);
      this._focusedView && isGroupRow($row) && this.setFocusedRowIndex(this._getRowIndex($row));
    } else {
      this._updateFocusedCellPosition(this._getCellElementFromTarget(target));
    }
  }
  _focusCell($cell, isDisabled) {
    if (this._isCellValid($cell)) {
      this._focus($cell, isDisabled);
      return true;
    }
    return;
  }
  _focusEditFormCell($cell) {
    if ($cell.hasClass(MASTER_DETAIL_CELL_CLASS)) {
      this._editorFactory.focus($cell, true);
    }
  }
  _resetFocusedCell(preventScroll) {
    var _this$_focusedView3;
    const $cell = this._getFocusedCell();
    isElementDefined($cell) && $cell.removeAttr("tabindex").removeClass(CELL_FOCUS_DISABLED_CLASS);
    this._isNeedFocus = false;
    this._isNeedScroll = false;
    this._focusedCellPosition = {};
    clearTimeout(this._updateFocusTimeout);
    null === (_this$_focusedView3 = this._focusedView) || void 0 === _this$_focusedView3 || _this$_focusedView3.renderFocusState({
      preventScroll: preventScroll
    });
  }
  restoreFocusableElement(rowIndex, $event) {
    const that = this;
    let args;
    let $rowElement;
    const isUpArrow = isDefined(rowIndex);
    const $rowsViewElement = this._rowsView.element();
    const {
      columnIndex: columnIndex
    } = that._focusedCellPosition;
    const rowIndexOffset = that._dataController.getRowIndexOffset();
    rowIndex = isUpArrow ? rowIndex : this._rowsView.getTopVisibleItemIndex() + rowIndexOffset;
    if (!isUpArrow) {
      that._editorFactory.loseFocus();
      that._applyTabIndexToElement($rowsViewElement);
      eventsEngine.trigger($rowsViewElement, "focus");
    } else {
      $rowElement = this._rowsView.getRow(rowIndex - rowIndexOffset);
      args = that._fireFocusedRowChanging($event, $rowElement);
      if (!args.cancel && args.rowIndexChanged) {
        rowIndex = args.newRowIndex;
      }
    }
    if (!isUpArrow || !args.cancel) {
      that.setFocusedCellPosition(rowIndex, columnIndex);
    }
    isUpArrow && that._updateFocus();
  }
  silentUpdateFocusedCellPosition(newFocusedCellPosition) {
    this._focusedCellPosition = _extends({}, this._focusedCellPosition ?? {}, newFocusedCellPosition);
  }
  _getNewPositionByCode(cellPosition, elementType, code) {
    let {
      columnIndex: columnIndex
    } = cellPosition;
    let {
      rowIndex: rowIndex
    } = cellPosition;
    let visibleColumnsCount;
    if (void 0 === cellPosition.rowIndex && "next" === code) {
      return {
        columnIndex: 0,
        rowIndex: 0
      };
    }
    switch (code) {
      case "nextInRow":
      case "next":
        visibleColumnsCount = this._getVisibleColumnCount();
        if (columnIndex < visibleColumnsCount - 1 && "row" !== elementType && this._hasValidCellAfterPosition({
          columnIndex: columnIndex,
          rowIndex: rowIndex
        })) {
          columnIndex++;
        } else if (!this._isLastRow(rowIndex) && "next" === code) {
          columnIndex = 0;
          rowIndex++;
        }
        break;
      case "previousInRow":
      case "previous":
        if (columnIndex > 0 && "row" !== elementType && this._hasValidCellBeforePosition({
          columnIndex: columnIndex,
          rowIndex: rowIndex
        })) {
          columnIndex--;
        } else if (rowIndex > 0 && "previous" === code) {
          rowIndex--;
          visibleColumnsCount = this._getVisibleColumnCount();
          columnIndex = visibleColumnsCount - 1;
        }
        break;
      case "upArrow":
        rowIndex = rowIndex > 0 ? rowIndex - 1 : rowIndex;
        break;
      case "downArrow":
        rowIndex = !this._isLastRow(rowIndex) ? rowIndex + 1 : rowIndex;
    }
    return {
      columnIndex: columnIndex,
      rowIndex: rowIndex
    };
  }
  getRowIndex() {
    return this._focusedCellPosition ? this._focusedCellPosition.rowIndex : -1;
  }
  getColumnIndex() {
    return this._focusedCellPosition ? this._focusedCellPosition.columnIndex : -1;
  }
  getVisibleRowIndex() {
    var _this$_focusedCellPos4;
    const rowIndex = null === (_this$_focusedCellPos4 = this._focusedCellPosition) || void 0 === _this$_focusedCellPos4 ? void 0 : _this$_focusedCellPos4.rowIndex;
    return !isDefined(rowIndex) || rowIndex < 0 ? -1 : rowIndex - this._dataController.getRowIndexOffset();
  }
  getVisibleColumnIndex() {
    var _this$_focusedCellPos5;
    const columnIndex = null === (_this$_focusedCellPos5 = this._focusedCellPosition) || void 0 === _this$_focusedCellPos5 ? void 0 : _this$_focusedCellPos5.columnIndex;
    return !isDefined(columnIndex) ? -1 : columnIndex - this._columnsController.getColumnIndexOffset();
  }
  _isCellByPositionValid(cellPosition) {
    const $cell = $(this._getCell(cellPosition));
    return this._isCellValid($cell);
  }
  _isLastRow(rowIndex) {
    const dataController = this._dataController;
    if (this._isVirtualRowRender()) {
      return rowIndex >= dataController.getMaxRowIndex();
    }
    const lastVisibleIndex = Math.max(...dataController.items().map((item, index) => false !== item.visible ? index : -1));
    return rowIndex === lastVisibleIndex;
  }
  _isFirstValidCell(cellPosition) {
    let isFirstValidCell = false;
    if (0 === cellPosition.rowIndex && cellPosition.columnIndex >= 0) {
      isFirstValidCell = isFirstValidCell || !this._hasValidCellBeforePosition(cellPosition);
    }
    return isFirstValidCell;
  }
  _hasValidCellBeforePosition(cellPosition) {
    let {
      columnIndex: columnIndex
    } = cellPosition;
    let hasValidCells = false;
    while (columnIndex > 0 && !hasValidCells) {
      const checkingPosition = {
        columnIndex: --columnIndex,
        rowIndex: cellPosition.rowIndex
      };
      hasValidCells = this._isCellByPositionValid(checkingPosition);
    }
    return hasValidCells;
  }
  _hasValidCellAfterPosition(cellPosition) {
    let {
      columnIndex: columnIndex
    } = cellPosition;
    let hasValidCells = false;
    const visibleColumnCount = this._getVisibleColumnCount();
    while (columnIndex < visibleColumnCount - 1 && !hasValidCells) {
      const checkingPosition = {
        columnIndex: ++columnIndex,
        rowIndex: cellPosition.rowIndex
      };
      hasValidCells = this._isCellByPositionValid(checkingPosition);
    }
    return hasValidCells;
  }
  _isLastValidCell(cellPosition) {
    const nextColumnIndex = cellPosition.columnIndex >= 0 ? cellPosition.columnIndex + 1 : 0;
    const {
      rowIndex: rowIndex
    } = cellPosition;
    const checkingPosition = {
      columnIndex: nextColumnIndex,
      rowIndex: rowIndex
    };
    const visibleRows = this._dataController.getVisibleRows();
    const row = visibleRows && visibleRows[rowIndex];
    const isLastRow = this._isLastRow(rowIndex);
    if (!isLastRow) {
      return false;
    }
    const isFullRowFocus = "group" === (null === row || void 0 === row ? void 0 : row.rowType) || "groupFooter" === (null === row || void 0 === row ? void 0 : row.rowType);
    if (isFullRowFocus && cellPosition.columnIndex > 0) {
      return true;
    }
    if (cellPosition.columnIndex === this._getVisibleColumnCount() - 1) {
      return true;
    }
    if (this._isCellByPositionValid(checkingPosition)) {
      return false;
    }
    return this._isLastValidCell(checkingPosition);
  }
  _isCellValid($cell, isClick) {
    if (isElementDefined($cell)) {
      const $row = $cell.parent();
      const columnIndex = this._rowsView.getCellIndex($cell) + this._columnsController.getColumnIndexOffset();
      const column = this._getColumnByCellElement($cell);
      const visibleColumnCount = this._getVisibleColumnCount();
      const editingController = this._editingController;
      const isMasterDetailRow = isDetailRow($row);
      const isShowWhenGrouped = column && column.showWhenGrouped;
      const isDataCell = column && !$cell.hasClass(COMMAND_EXPAND_CLASS) && isDataRow($row);
      const isValidGroupSpaceColumn = function () {
        return !isMasterDetailRow && column && (!isDefined(column.groupIndex) || isShowWhenGrouped && isDataCell) || parseInt($cell.attr("colspan"), 10) > 1;
      };
      const isDragCell = GridCoreKeyboardNavigationDom.isDragCell($cell);
      if (isDragCell) {
        return false;
      }
      if (this.getMasterDetailCell($cell)) {
        return true;
      }
      if (visibleColumnCount > columnIndex && isValidGroupSpaceColumn()) {
        const rowItems = this._dataController.items();
        const visibleRowIndex = this._rowsView.getRowIndex($row);
        const row = rowItems[visibleRowIndex];
        const isCellEditing = editingController && this._isCellEditMode() && editingController.isEditing();
        const isRowEditingInCurrentRow = editingController && editingController.isEditRow(visibleRowIndex);
        const isEditing = isRowEditingInCurrentRow || isCellEditing;
        if (column.command) {
          if (this._isLegacyNavigation()) {
            return !isEditing && "expand" === column.command;
          }
          if (isCellEditing) {
            return false;
          }
          if (isRowEditingInCurrentRow) {
            return "select" !== column.command;
          }
          return !isEditing;
        }
        if (isCellEditing && row && "data" !== row.rowType) {
          return false;
        }
        return !isEditing || column.allowEditing || isClick;
      }
    }
  }
  getFirstValidCellInRow($row, columnIndex) {
    const that = this;
    const $cells = $row.find("> td");
    let $cell;
    let $result;
    columnIndex = columnIndex || 0;
    for (let i = columnIndex; i < $cells.length; ++i) {
      $cell = $cells.eq(i);
      if (that._isCellValid($cell)) {
        $result = $cell;
        break;
      }
    }
    return $result;
  }
  _getNextCell(keyCode, elementType, cellPosition) {
    const focusedCellPosition = cellPosition || this._focusedCellPosition;
    const isRowFocusType = this.isRowFocusType();
    const includeCommandCells = isRowFocusType || ["next", "previous"].includes(keyCode);
    let $cell;
    let $row;
    if (this._focusedView && focusedCellPosition) {
      const newFocusedCellPosition = this._getNewPositionByCode(focusedCellPosition, elementType, keyCode);
      $cell = $(this._getCell(newFocusedCellPosition));
      const isLastCellOnDirection = "previous" === keyCode ? this._isFirstValidCell(newFocusedCellPosition) : this._isLastValidCell(newFocusedCellPosition);
      if (isElementDefined($cell) && !this._isCellValid($cell) && this._isCellInRow(newFocusedCellPosition, includeCommandCells) && !isLastCellOnDirection) {
        if (isRowFocusType) {
          $cell = this.getFirstValidCellInRow($cell.parent(), newFocusedCellPosition.columnIndex);
        } else {
          $cell = this._getNextCell(keyCode, "cell", newFocusedCellPosition);
        }
      }
      $row = isElementDefined($cell) && $cell.parent();
      if (this._hasSkipRow($row)) {
        const rowIndex = this._getRowIndex($row);
        if (!this._isLastRow(rowIndex)) {
          $cell = this._getNextCell(keyCode, "row", {
            columnIndex: focusedCellPosition.columnIndex,
            rowIndex: rowIndex
          });
        } else {
          return null;
        }
      }
      return isElementDefined($cell) ? $cell : null;
    }
    return null;
  }
  _startEditing(eventArgs, fastEditingKey) {
    const focusedCellPosition = this._focusedCellPosition;
    const visibleRowIndex = this.getVisibleRowIndex();
    const visibleColumnIndex = this.getVisibleColumnIndex();
    const row = this._dataController.items()[visibleRowIndex];
    const column = this._columnsController.getVisibleColumns()[visibleColumnIndex];
    if (this._isAllowEditing(row, column)) {
      if (this._isRowEditMode()) {
        this._editingController.editRow(visibleRowIndex);
      } else if (focusedCellPosition) {
        this._startEditCell(eventArgs, fastEditingKey);
      }
    }
  }
  _isAllowEditing(row, column) {
    return this._editingController.allowUpdating({
      row: row
    }) && column && column.allowEditing;
  }
  _editFocusedCell() {
    const rowIndex = this.getVisibleRowIndex();
    const colIndex = this.getVisibleColumnIndex();
    return this._editingController.editCell(rowIndex, colIndex);
  }
  _startEditCell(eventArgs, fastEditingKey) {
    this._fastEditingStarted = isDefined(fastEditingKey);
    const editResult = this._editFocusedCell();
    const isEditResultDeferred = isDeferred(editResult);
    const isFastEditingStarted = this._isFastEditingStarted();
    if (!isFastEditingStarted || !isEditResultDeferred && !editResult) {
      return;
    }
    const editorValue = isEditResultDeferred && fastEditingKey === FAST_EDITING_DELETE_KEY ? "" : fastEditingKey;
    const editResultDeferred = isEditResultDeferred ? editResult : Deferred().resolve();
    const waitTemplatesDeferred = this._rowsView.waitAsyncTemplates(true);
    when(editResultDeferred, waitTemplatesDeferred).done(() => {
      this._editingCellHandler(eventArgs, editorValue);
    });
  }
  _editingCellHandler(eventArgs, editorValue) {
    var _$inputElement$select;
    const $input = this._getFocusedCell().find(INTERACTIVE_ELEMENTS_SELECTOR).eq(0);
    const $inputElement = $input.get(0);
    if (!$inputElement) {
      return;
    }
    const keyDownEvent = createEvent(eventArgs, {
      type: "keydown",
      target: $inputElement
    });
    const keyPressEvent = createEvent(eventArgs, {
      type: "keypress",
      target: $inputElement
    });
    const inputEvent = createEvent(eventArgs, {
      type: "input",
      target: $inputElement
    });
    if (inputEvent.originalEvent) {
      inputEvent.originalEvent = createEvent(inputEvent.originalEvent, {
        data: editorValue
      });
    }
    null === (_$inputElement$select = $inputElement.select) || void 0 === _$inputElement$select || _$inputElement$select.call($inputElement);
    eventsEngine.trigger($input, keyDownEvent);
    if (!keyDownEvent.isDefaultPrevented()) {
      eventsEngine.trigger($input, keyPressEvent);
      if (!keyPressEvent.isDefaultPrevented()) {
        const timeout = browser.mozilla ? 25 : 0;
        setTimeout(() => {
          const inputValue = this._getKeyPressInputValue($input, editorValue);
          $input.val(inputValue);
          const $widgetContainer = $input.closest(`.${WIDGET_CLASS}`);
          eventsEngine.off($widgetContainer, "focusout");
          eventsEngine.one($widgetContainer, "focusout", () => {
            eventsEngine.trigger($input, "change");
          });
          eventsEngine.trigger($input, inputEvent);
        }, timeout);
      }
    }
  }
  _getKeyPressInputValue($input, editorValue) {
    const inputCurrentValue = $input.val();
    return "-" === editorValue && "-0" === inputCurrentValue ? "-0" : editorValue;
  }
  _fireFocusChangingEvents($event, $cell, fireRowEvent, isHighlighted) {
    let args = {};
    const cellPosition = this._getCellPosition($cell) ?? {};
    if (this.isCellFocusType()) {
      args = this._fireFocusedCellChanging($event, $cell, isHighlighted);
      if (!args.cancel) {
        cellPosition.columnIndex = args.newColumnIndex;
        cellPosition.rowIndex = args.newRowIndex;
        isHighlighted = args.isHighlighted;
        $cell = $(this._getCell(cellPosition));
      }
    }
    if (!args.cancel && fireRowEvent && $cell) {
      args = this._fireFocusedRowChanging($event, $cell.parent());
      if (!args.cancel) {
        cellPosition.rowIndex = args.newRowIndex;
        args.isHighlighted = isHighlighted;
      }
    }
    args.$newCellElement = $(this._getCell(cellPosition));
    if (!args.$newCellElement.length) {
      args.$newCellElement = $cell;
    }
    return args;
  }
  _fireFocusedCellChanging($event, $cellElement, isHighlighted) {
    const prevColumnIndex = this.option("focusedColumnIndex");
    const prevRowIndex = this.option("focusedRowIndex");
    const cellPosition = this._getCellPosition($cellElement);
    const columnIndex = cellPosition ? cellPosition.columnIndex : -1;
    const rowIndex = cellPosition ? cellPosition.rowIndex : -1;
    const visibleRows = this._dataController.getVisibleRows();
    const visibleColumns = this._columnsController.getVisibleColumns();
    const args = {
      cellElement: $cellElement,
      prevColumnIndex: prevColumnIndex,
      prevRowIndex: prevRowIndex,
      newColumnIndex: columnIndex,
      newRowIndex: rowIndex,
      rows: visibleRows,
      columns: visibleColumns,
      event: $event,
      isHighlighted: isHighlighted || false,
      cancel: false
    };
    this._canceledCellPosition = null;
    this.executeAction("onFocusedCellChanging", args);
    if (args.newColumnIndex !== columnIndex || args.newRowIndex !== rowIndex) {
      args.$newCellElement = $(this._getCell({
        columnIndex: args.newColumnIndex,
        rowIndex: args.newRowIndex
      }));
    }
    if (args.cancel) {
      this._canceledCellPosition = {
        rowIndex: rowIndex,
        columnIndex: columnIndex
      };
    }
    return args;
  }
  _fireFocusedCellChanged($cell) {
    const columnIndex = this._rowsView.getCellIndex($cell);
    const rowOptions = null === $cell || void 0 === $cell ? void 0 : $cell.parent().data("options");
    const focusedRowKey = null === rowOptions || void 0 === rowOptions ? void 0 : rowOptions.key;
    this._memoFireFocusedCellChanged(focusedRowKey, columnIndex);
  }
  _memoFireFocusedCellChanged(rowKey, columnIndex) {
    const $cell = this._getFocusedCell();
    const $row = null === $cell || void 0 === $cell ? void 0 : $cell.parent();
    const rowIndex = this.getRowIndex();
    const localRowIndex = Math.min(this._getLocalRowIndex($row), this._dataController.items().length - 1);
    const isEditingCell = this._editingController.isEditCell(localRowIndex, columnIndex);
    if (isEditingCell) {
      return;
    }
    const row = this._dataController.items()[localRowIndex];
    const column = this._columnsController.getVisibleColumns()[columnIndex];
    this.executeAction("onFocusedCellChanged", {
      cellElement: $cell ? getPublicElement($cell) : void 0,
      columnIndex: columnIndex,
      rowIndex: rowIndex,
      row: row,
      column: column
    });
  }
  _fireFocusedRowChanging(eventArgs, $newFocusedRow) {
    const newRowIndex = this._getRowIndex($newFocusedRow);
    const prevFocusedRowIndex = this.option("focusedRowIndex");
    const loadingOperationTypes = this._dataController.loadingOperationTypes();
    const args = {
      rowElement: $newFocusedRow,
      prevRowIndex: prevFocusedRowIndex,
      newRowIndex: newRowIndex,
      event: eventArgs,
      rows: this._dataController.getVisibleRows(),
      cancel: false
    };
    const loadingOperations = loadingOperationTypes.sorting || loadingOperationTypes.grouping || loadingOperationTypes.filtering || loadingOperationTypes.paging;
    if (!this._dataController || this._dataController.isLoading() && loadingOperations) {
      args.cancel = true;
      return args;
    }
    if (this.option("focusedRowEnabled")) {
      this.executeAction("onFocusedRowChanging", args);
      if (!args.cancel && args.newRowIndex !== newRowIndex) {
        args.resetFocusedRow = args.newRowIndex < 0;
        if (!args.resetFocusedRow) {
          this.setFocusedRowIndex(args.newRowIndex);
        }
        args.rowIndexChanged = true;
      }
    }
    return args;
  }
  _fireFocusedRowChanged() {
    var _this$_focusControlle;
    const focusedRowEnabled = this.option("focusedRowEnabled");
    const focusedRowKey = this.option("focusedRowKey");
    const focusedRowIndex = null === (_this$_focusControlle = this._focusController) || void 0 === _this$_focusControlle ? void 0 : _this$_focusControlle.getFocusedRowIndexByKey(focusedRowKey);
    if (!focusedRowEnabled || isDefined(focusedRowKey) && focusedRowIndex < 0) {
      return;
    }
    this._memoFireFocusedRowChanged(focusedRowKey, focusedRowIndex);
  }
  _memoFireFocusedRowChanged(focusedRowKey, focusedRowIndex) {
    const localRowIndex = focusedRowIndex - this._dataController.getRowIndexOffset();
    this.executeAction("onFocusedRowChanged", {
      rowElement: focusedRowIndex < 0 ? void 0 : this._rowsView.getRowElement(localRowIndex),
      rowIndex: focusedRowIndex,
      row: focusedRowIndex < 0 ? void 0 : this._dataController.getVisibleRows()[localRowIndex]
    });
  }
  _isEventInCurrentGrid(event) {
    return gridCoreUtils.isElementInCurrentGrid(this, $(event.target));
  }
  _isRowEditMode() {
    const editMode = this._editingController.getEditMode();
    return editMode === EDIT_MODE_ROW || editMode === EDIT_MODE_FORM;
  }
  _isCellEditMode() {
    const editMode = this._editingController.getEditMode();
    return editMode === EDIT_MODE_CELL || editMode === EDIT_MODE_BATCH;
  }
  _isFastEditingAllowed() {
    return this._isCellEditMode() && this.option("keyboardNavigation.editOnKeyPress");
  }
  _applyTabIndexToElement($element) {
    const tabIndex = this.option("tabIndex") ?? 0;
    $element.attr("tabindex", tabIndex);
  }
  _getCell(cellPosition) {
    if (this._focusedView && cellPosition) {
      const rowIndexOffset = this._dataController.getRowIndexOffset();
      const column = this._columnsController.getVisibleColumns(null, true)[cellPosition.columnIndex];
      const columnIndexOffset = column && column.fixed ? this._getFixedColumnIndexOffset(column) : this._columnsController.getColumnIndexOffset();
      const rowIndex = cellPosition.rowIndex >= 0 ? cellPosition.rowIndex - rowIndexOffset : -1;
      const columnIndex = cellPosition.columnIndex >= 0 ? cellPosition.columnIndex - columnIndexOffset : -1;
      return this._focusedView.getCell({
        rowIndex: rowIndex,
        columnIndex: columnIndex
      });
    }
  }
  _getRowIndex($row) {
    let rowIndex = this._getLocalRowIndex($row);
    if (rowIndex >= 0) {
      rowIndex += this._dataController.getRowIndexOffset();
    }
    return rowIndex;
  }
  _getLocalRowIndex($row) {
    const rowIndex = this._rowsView.getRowIndex($row);
    return rowIndex;
  }
  getCellIndex($cell, rowIndex) {
    return this._rowsView.getCellIndex($cell, rowIndex);
  }
  _hasSkipRow($row) {
    const row = $row && $row.get(0);
    return row && "none" === row.style.display;
  }
  _allowEditingOnEnterKey() {
    return "startEdit" === this.option("keyboardNavigation.enterKeyAction");
  }
  _isLegacyNavigation() {
    return this.option("useLegacyKeyboardNavigation");
  }
  _getDirectionCodeByKey(key) {
    let directionCode;
    switch (key) {
      case "upArrow":
        directionCode = "prevRow";
        break;
      case "downArrow":
        directionCode = "nextRow";
        break;
      case "leftArrow":
        directionCode = this.option("rtlEnabled") ? "nextInRow" : "previousInRow";
        break;
      case "rightArrow":
        directionCode = this.option("rtlEnabled") ? "previousInRow" : "nextInRow";
    }
    return directionCode;
  }
  _isVirtualScrolling() {
    const scrollingMode = this.option("scrolling.mode");
    return "virtual" === scrollingMode || "infinite" === scrollingMode;
  }
  _isVirtualRowRender() {
    return this._isVirtualScrolling() || gridCoreUtils.isVirtualRowRendering(this);
  }
  _isVirtualColumnRender() {
    return "virtual" === this.option("scrolling.columnRenderingMode");
  }
  _scrollBy(left, top, rowIndex, $event) {
    const that = this;
    const scrollable = this._rowsView.getScrollable();
    if (that._focusedCellPosition) {
      const scrollHandler = function () {
        scrollable.off("scroll", scrollHandler);
        setTimeout(that.restoreFocusableElement.bind(that, rowIndex, $event));
      };
      scrollable.on("scroll", scrollHandler);
    }
    return scrollable.scrollBy({
      left: left,
      top: top
    });
  }
  _isInsideEditForm(element) {
    const $editForm = $(element).closest(`.${this.addWidgetPrefix(EDIT_FORM_CLASS)}`);
    return $editForm.length && this.elementIsInsideGrid($editForm);
  }
  getMasterDetailCell(element) {
    const $masterDetailCell = $(element).closest(`.${MASTER_DETAIL_CELL_CLASS}`);
    if ($masterDetailCell.length && this.elementIsInsideGrid($masterDetailCell)) {
      return $masterDetailCell;
    }
    return null;
  }
  _processNextCellInMasterDetail($nextCell, _$cell) {
    if (!this._isInsideEditForm($nextCell) && $nextCell) {
      this._applyTabIndexToElement($nextCell);
    }
  }
  _handleTabKeyOnMasterDetailCell(target, direction) {
    if (this.getMasterDetailCell(target)) {
      this._updateFocusedCellPosition($(target), direction);
      const $nextCell = this._getNextCell(direction, "row");
      this._processNextCellInMasterDetail($nextCell, $(target));
      return true;
    }
    return false;
  }
  _getElementType(target) {
    return $(target).is("tr") ? "row" : "cell";
  }
  _isFastEditingStarted() {
    return this._isFastEditingAllowed() && this._fastEditingStarted;
  }
  _getVisibleColumnCount() {
    return this._columnsController.getVisibleColumns(null, true).length;
  }
  _isCellInRow(cellPosition, includeCommandCells) {
    const {
      columnIndex: columnIndex
    } = cellPosition;
    const visibleColumnsCount = this._getVisibleColumnCount();
    return includeCommandCells ? columnIndex >= 0 && columnIndex <= visibleColumnsCount - 1 : columnIndex > 0 && columnIndex < visibleColumnsCount - 1;
  }
  _isCellElement($element) {
    return $element.length && "TD" === $element[0].tagName;
  }
  _getCellElementFromTarget(target) {
    const elementType = this._getElementType(target);
    const $targetElement = $(target);
    let $cell;
    if ("cell" === elementType) {
      $cell = $targetElement.closest(`.${ROW_CLASS} > td`);
    } else {
      $cell = $targetElement.children().not(`.${COMMAND_EXPAND_CLASS}`).first();
    }
    return $cell;
  }
  _getRowsViewElement() {
    var _this$_rowsView7;
    return null === (_this$_rowsView7 = this._rowsView) || void 0 === _this$_rowsView7 ? void 0 : _this$_rowsView7.element();
  }
  _processCanceledEditCellPosition(rowIndex, columnIndex) {
    if (this._canceledCellPosition) {
      const isCanceled = this._canceledCellPosition.rowIndex === rowIndex && this._canceledCellPosition.columnIndex === columnIndex;
      this._canceledCellPosition = null;
      return isCanceled;
    }
    return;
  }
  updateFocusedRowIndex() {
    const dataController = this._dataController;
    const visibleRowIndex = this.getVisibleRowIndex();
    const visibleItems = dataController.items();
    const lastVisibleIndex = visibleItems.length ? visibleItems.length - 1 : -1;
    const rowIndexOffset = dataController.getRowIndexOffset();
    if (lastVisibleIndex >= 0 && visibleRowIndex > lastVisibleIndex) {
      this.setFocusedRowIndex(lastVisibleIndex + rowIndexOffset);
    }
  }
  needNavigationToCell() {
    return this._needNavigationToCell;
  }
  navigationToCellInProgress() {
    return this.needToRestoreFocus || this.needNavigationToCell();
  }
}
const rowsView = Base => class extends Base {
  _rowClick(e) {
    const editRowIndex = this._editingController.getEditRowIndex();
    const isKeyboardEnabled = this._keyboardNavigationController.isKeyboardEnabled();
    if (editRowIndex === e.rowIndex) {
      this._keyboardNavigationController.setCellFocusType();
    }
    const needTriggerPointerEventHandler = (isMobile() || !isKeyboardEnabled) && this.option("focusedRowEnabled");
    if (needTriggerPointerEventHandler) {
      this._triggerPointerDownEventHandler(e, !isKeyboardEnabled);
    }
    super._rowClick.apply(this, arguments);
  }
  _triggerPointerDownEventHandler(e, force) {
    const {
      originalEvent: originalEvent
    } = e.event;
    if (originalEvent) {
      const $cell = $(originalEvent.target);
      const columnIndex = this.getCellIndex($cell);
      const column = this._columnsController.getVisibleColumns()[columnIndex];
      const row = this._dataController.items()[e.rowIndex];
      if (this._keyboardNavigationController._isAllowEditing(row, column) || force) {
        const eventArgs = createEvent(originalEvent, {
          currentTarget: originalEvent.target
        });
        this._keyboardNavigationController._pointerEventHandler(eventArgs);
      }
    }
  }
  renderFocusState(params) {
    super.renderFocusState(params);
    if (this._keyboardNavigationController.navigationToCellInProgress()) {
      return;
    }
    const {
      preventScroll: preventScroll,
      pageSizeChanged: pageSizeChanged
    } = params ?? {};
    const $rowsViewElement = this.element();
    if ($rowsViewElement && !focused($rowsViewElement)) {
      $rowsViewElement.attr("tabindex", null);
    }
    pageSizeChanged && this._keyboardNavigationController.updateFocusedRowIndex();
    let rowIndex = this._keyboardNavigationController.getVisibleRowIndex();
    if (!isDefined(rowIndex) || rowIndex < 0) {
      rowIndex = 0;
    }
    const cellElements = this.getCellElements(rowIndex);
    if (this._keyboardNavigationController.isKeyboardEnabled() && null !== cellElements && void 0 !== cellElements && cellElements.length) {
      this.updateFocusElementTabIndex(cellElements, preventScroll);
    }
  }
  updateFocusElementTabIndex(cellElements, preventScroll) {
    const $row = cellElements.eq(0).parent();
    if (isGroupRow($row)) {
      this._keyboardNavigationController._applyTabIndexToElement($row);
    } else {
      let columnIndex = this._keyboardNavigationController.getColumnIndex();
      if (!isDefined(columnIndex) || columnIndex < 0) {
        columnIndex = 0;
      }
      this._updateFocusedCellTabIndex(cellElements, columnIndex);
    }
  }
  _updateFocusedCellTabIndex(cellElements, columnIndex) {
    const keyboardController = this._keyboardNavigationController;
    const cellElementsLength = cellElements ? cellElements.length : -1;
    const updateCellTabIndex = function ($cell) {
      const isMasterDetailCell = !!keyboardController.getMasterDetailCell($cell);
      const isValidCell = keyboardController._isCellValid($cell);
      if (!isMasterDetailCell && isValidCell && keyboardController._isCellElement($cell)) {
        keyboardController._applyTabIndexToElement($cell);
        keyboardController.setCellFocusType();
        return true;
      }
      return;
    };
    const $cell = GridCoreKeyboardNavigationDom.getCellToFocus(cellElements, columnIndex);
    if ($cell.length) {
      updateCellTabIndex($cell);
    } else {
      if (cellElementsLength <= columnIndex) {
        columnIndex = cellElementsLength - 1;
      }
      for (let i = columnIndex; i < cellElementsLength; ++i) {
        if (updateCellTabIndex($(cellElements[i]))) {
          break;
        }
      }
    }
  }
  renderDelayedTemplates(change) {
    super.renderDelayedTemplates.apply(this, arguments);
    this.waitAsyncTemplates().done(() => {
      this._renderFocusByChange(change);
    });
  }
  _renderFocusByChange(change) {
    const {
      operationTypes: operationTypes,
      repaintChangesOnly: repaintChangesOnly
    } = change ?? {};
    const {
      fullReload: fullReload,
      pageSize: pageSize
    } = operationTypes ?? {};
    if (!change || !repaintChangesOnly || fullReload || pageSize) {
      const preventScroll = shouldPreventScroll(this);
      this.renderFocusState({
        preventScroll: preventScroll,
        pageSizeChanged: pageSize
      });
    }
  }
  _renderCore(change) {
    const deferred = super._renderCore.apply(this, arguments);
    this._renderFocusByChange(change);
    return deferred;
  }
  _editCellPrepared($cell) {
    var _this$_keyboardNaviga;
    const editorInstance = this._getEditorInstance($cell);
    const isEditingNavigationMode = null === (_this$_keyboardNaviga = this._keyboardNavigationController) || void 0 === _this$_keyboardNaviga ? void 0 : _this$_keyboardNaviga._isFastEditingStarted();
    if (editorInstance && isEditingNavigationMode) {
      this._handleEditingNavigationMode(editorInstance);
    }
    super._editCellPrepared.apply(this, arguments);
  }
  _handleEditingNavigationMode(editorInstance) {
    ["downArrow", "upArrow"].forEach(keyName => {
      const originalKeyHandler = editorInstance._supportedKeys()[keyName];
      editorInstance.registerKeyHandler(keyName, e => {
        const isDropDownOpened = "true" === editorInstance._input().attr("aria-expanded");
        if (isDropDownOpened) {
          return originalKeyHandler && originalKeyHandler.call(editorInstance, e);
        }
      });
    });
    editorInstance.registerKeyHandler("leftArrow", noop);
    editorInstance.registerKeyHandler("rightArrow", noop);
    const isDateBoxWithMask = editorInstance.NAME === DATEBOX_WIDGET_NAME && editorInstance.option("useMaskBehavior");
    if (isDateBoxWithMask) {
      editorInstance.registerKeyHandler("enter", noop);
    }
  }
  _getEditorInstance($cell) {
    const $editor = $cell.find(".dx-texteditor").eq(0);
    return gridCoreUtils.getWidgetInstance($editor);
  }
  _handleScroll(e) {
    super._handleScroll(e);
    if (this._keyboardNavigationController.needNavigationToCell()) {
      this._keyboardNavigationController.navigateToFirstOrLastCell(this._keyboardNavigationController.isQuickNavigationToFirstCell());
    }
  }
  init() {
    super.init();
    this._resizeController = this.getController("resizing");
  }
};
const editing = Base => class extends Base {
  editCell(rowIndex, columnIndex) {
    if (this._keyboardNavigationController._processCanceledEditCellPosition(rowIndex, columnIndex)) {
      return false;
    }
    const isCellEditing = super.editCell(rowIndex, columnIndex);
    if (isCellEditing) {
      this._keyboardNavigationController.setupFocusedView();
    }
    return isCellEditing;
  }
  editRow(rowIndex) {
    const visibleColumnIndex = this._keyboardNavigationController.getVisibleColumnIndex();
    const column = this._columnsController.getVisibleColumns()[visibleColumnIndex];
    if (column && column.type || this.option("editing.mode") === EDIT_MODE_FORM) {
      this._keyboardNavigationController._resetFocusedCell();
    }
    super.editRow(rowIndex);
    return;
  }
  addRow(parentKey) {
    this._keyboardNavigationController.setupFocusedView();
    this._keyboardNavigationController.setCellFocusType();
    return super.addRow.apply(this, arguments);
  }
  getFocusedCellInRow(rowIndex) {
    let $cell = super.getFocusedCellInRow(rowIndex);
    const rowIndexOffset = this._dataController.getRowIndexOffset();
    const focusedRowIndex = this._keyboardNavigationController._focusedCellPosition.rowIndex - rowIndexOffset;
    if (this._keyboardNavigationController.isKeyboardEnabled() && focusedRowIndex === rowIndex) {
      const $focusedCell = this._keyboardNavigationController._getFocusedCell();
      if (isElementDefined($focusedCell) && !$focusedCell.hasClass(COMMAND_EDIT_CLASS)) {
        $cell = $focusedCell;
      }
    }
    return $cell;
  }
  _processCanceledEditingCell() {
    this.closeEditCell().done(() => {
      this._keyboardNavigationController._updateFocus();
    });
  }
  closeEditCell() {
    const keyboardNavigation = this._keyboardNavigationController;
    keyboardNavigation._fastEditingStarted = false;
    const result = super.closeEditCell.apply(this, arguments);
    const $focusedElement = this._getFocusedElement();
    const isFilterCell = !!$focusedElement.closest(`.${this.addWidgetPrefix(FILTER_ROW_CLASS)}`).length;
    if (!isFilterCell) {
      keyboardNavigation._updateFocus();
    }
    return result;
  }
  _getFocusedElement() {
    var _this$component$eleme, _this$component;
    const $element = $(null === (_this$component$eleme = (_this$component = this.component).element) || void 0 === _this$component$eleme ? void 0 : _this$component$eleme.call(_this$component));
    const $focusedElement = $element.find(":focus");
    return $focusedElement;
  }
  _delayedInputFocus() {
    this._keyboardNavigationController._isNeedScroll = true;
    super._delayedInputFocus.apply(this, arguments);
  }
  _isEditingStart() {
    const cancel = super._isEditingStart.apply(this, arguments);
    if (cancel && !this._keyboardNavigationController._isNeedFocus) {
      const $cell = this._keyboardNavigationController._getFocusedCell();
      this._keyboardNavigationController._focus($cell, true);
    }
    return cancel;
  }
};
const data = Base => class extends Base {
  _correctRowIndices(getRowIndexCorrection) {
    const focusedCellPosition = this._keyboardNavigationController._focusedCellPosition;
    super._correctRowIndices.apply(this, arguments);
    if (focusedCellPosition && focusedCellPosition.rowIndex >= 0) {
      const focusedRowIndexCorrection = getRowIndexCorrection(focusedCellPosition.rowIndex);
      if (focusedRowIndexCorrection) {
        focusedCellPosition.rowIndex += focusedRowIndexCorrection;
        this._editorFactoryController.refocus();
      }
    }
  }
  getMaxRowIndex() {
    let result = this.items().length - 1;
    const virtualItemsCount = this.virtualItemsCount();
    if (virtualItemsCount) {
      const rowIndexOffset = this.getRowIndexOffset();
      result += rowIndexOffset + virtualItemsCount.end;
    }
    return result;
  }
};
const adaptiveColumns = Base => class extends Base {
  _showHiddenCellsInView(_ref) {
    let {
      viewName: viewName,
      $cells: $cells,
      isCommandColumn: isCommandColumn
    } = _ref;
    super._showHiddenCellsInView.apply(this, arguments);
    viewName === COLUMN_HEADERS_VIEW && !isCommandColumn && $cells.each((_, cellElement) => {
      const $cell = $(cellElement);
      isCellInHeaderRow($cell) && $cell.attr("tabindex", 0);
    });
  }
  _hideVisibleCellInView(_ref2) {
    let {
      viewName: viewName,
      $cell: $cell,
      isCommandColumn: isCommandColumn
    } = _ref2;
    super._hideVisibleCellInView.apply(this, arguments);
    if (viewName === COLUMN_HEADERS_VIEW && !isCommandColumn && isCellInHeaderRow($cell)) {
      $cell.removeAttr("tabindex");
    }
  }
  _hideVisibleColumnInView(_ref3) {
    let {
      view: view,
      isCommandColumn: isCommandColumn,
      visibleIndex: visibleIndex
    } = _ref3;
    super._hideVisibleColumnInView({
      view: view,
      isCommandColumn: isCommandColumn,
      visibleIndex: visibleIndex
    });
    if (view.name === ROWS_VIEW) {
      this._rowsView.renderFocusState({
        preventScroll: shouldPreventScroll(this)
      });
    }
  }
};
export const keyboardNavigationModule = {
  defaultOptions: () => ({
    useLegacyKeyboardNavigation: false,
    keyboardNavigation: {
      enabled: true,
      enterKeyAction: "startEdit",
      enterKeyDirection: "none",
      editOnKeyPress: false
    }
  }),
  controllers: {
    keyboardNavigation: KeyboardNavigationController
  },
  extenders: {
    views: {
      rowsView: rowsView
    },
    controllers: {
      editing: editing,
      data: data,
      adaptiveColumns: adaptiveColumns,
      keyboardNavigation: keyboardNavigationScrollableA11yExtender
    }
  }
};